import React, { useEffect, useState } from "react";
import {
  styled,
  Box,
  TableRow,
  TableCell,
  TableBody,
  Table,
  IconButton,
} from "@mui/material";
import { colors, fontSize, fontWeight, padding } from "theme/defaultStyle";
import { FileUploadStatus } from "entities/UIModel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import TooltipCell from "controls/global/stewart-table/TooltipCell";
import CustomTooltip from "controls/global/custom-tooltip/CustomTooltip";
import { showToast } from "controls/global/stewart-toast/showToast";

import CustomTextField from "controls/global/text-input-field/CustomTextField";
import { FormProvider, useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { UWADocument as UiUWADocument } from "entities/UIModel/UWADocument";
import { UWADocument as ApiUWADocument } from "entities/ApiModel/UWADocument";
import { ActionResultStatus, UIConstants } from "utils/data/enum";
import { useDocument } from "utils/context/DocumentContext";
import { useAutomaticProgressDialogActions } from "utils/context/AutomaticProgressDialogContext";
import { useUWAActions } from "utils/context/UWAContext";
import ConfirmationDialogV2 from "controls/global/dialogs/confirmation-dialog/ConfirmationDialogV2";


const StyledTableContainer = styled(Box)({
  overflowX: "auto",      // horizontal scroll if needed
  overflowY: "auto",      // vertical scroll if needed
  maxWidth: "100%",       // prevent table from exceeding parent width  

  // "& td.file-name": {
  //   width: "70%",
  // },
});

const StyledTableRow = styled(TableRow)({
  "&.is-editing td": {
    backgroundColor: "#E9F8FF",
    borderTop: "1px solid #B5D8FF",
    borderBottom: "1px solid #B5D8FF",
  },
  "&.is-editing td:first-of-type": {
    borderLeft: "1px solid #B5D8FF",
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
    paddingLeft: 6,
  },
  "&.is-editing td:last-of-type": {
    borderRight: "1px solid #B5D8FF",
    borderTopRightRadius: 6,
    borderBottomRightRadius: 6,
    paddingRight: 6,
  },
});

const EditField = styled(CustomTextField)({
  "&&": { width: "100%", maxWidth: "none", minWidth: 260 },
  "&& .MuiOutlinedInput-root": { backgroundColor: "#FFFFFF !important", height: 34 },
  "&& .MuiInputBase-input": { padding: "6px 8px" },
});

const StyledTableCell = styled(TableCell)({
  fontSize: fontSize.large,
  fontWeight: fontWeight.normal1,
  padding: padding.zero,
  "&:first-child": { paddingLeft: padding.small2, width: "60%" },
  "& .icon-enabled": { color: colors.blue01 },
  "& .icon-disabled": { color: colors.blue11 },
});

type Props = {
  uploadRequestId?: string | number;
  documents: UiUWADocument[];
  fileUploadStatuses?: FileUploadStatus[];
  handleOnDelete?: (documentId: number) => void;
  handleOnRename?: (documentId: number, newName: string) => void;
  className?: string;
  hideMessage?: boolean;
  hideDelete?: boolean;
  onEditModeChange?: (isEditing: boolean) => void;
  onSaveDocument?: () => Promise<void>;
};

type RenameForm = {
  documentDisplayFileNameSendDoc: string;
  documentDisplayEditedNameMap: Record<string, string>; 
};

function splitBaseAndExt(filename?: string) {
  if (!filename) return { base: "", ext: "" };
  const lastDot = filename.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === filename.length - 1)
    return { base: filename, ext: "" };
  return {
    base: filename.substring(0, lastDot),
    ext: filename.substring(lastDot + 1),
  };
}

const schema = yup.object({
  documentDisplayFileNameSendDoc: yup.string().trim().required("Required field"),
  documentDisplayEditedNameMap: yup.mixed<Record<string, string>>().optional(),
});

const UploadStatusGridEdit: React.FC<Props> = ({
  documents,
  fileUploadStatuses,
  handleOnDelete,
  handleOnRename,
  className,
  hideMessage = false,
  hideDelete = false,
  onEditModeChange,
  onSaveDocument,
}) => {


  const [rows, setRows] = useState<UiUWADocument[]>(documents ?? []);
  const [, { saveUWADocument }] = useDocument();
  const [, { openAutomaticProgressDialog, closeAutomaticProgressDialog }] = useAutomaticProgressDialogActions();
  const SAVE_DOCUMENT_NAME_SUCCESS_TITLE = "Document name edited successfully";
  const [saveErrorMessage, setSaveErrorMessage] = useState<string>("");
  const [openSaveErrorDialog, setOpenSaveErrorDialog] = useState<boolean>(false);
  const [, { setUWAGridInEditMode }] = useUWAActions();


  useEffect(() => {
    setRows(documents ?? []);
  }, [documents]);

  const methods = useForm<RenameForm>({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      documentDisplayFileNameSendDoc: "",
      documentDisplayEditedNameMap: {},
    },
  });

  useEffect(() => {
    methods.register("documentDisplayEditedNameMap" as const);
  }, [methods]);

  const [editRowId, setEditRowId] = React.useState<number | null>(null);
  const { documentDisplayFileNameSendDoc } = methods.getValues();

  const [currentEditingRow, setCurrentEditingRow] =
    useState<UiUWADocument | null>(null);

  const originalBaseRef = React.useRef<string>("");

  const statusById = React.useMemo(() => {
    const m = new Map<number, FileUploadStatus>();
    (fileUploadStatuses ?? []).forEach((s: any) => {
      const id =
        typeof s.documentId === "number"
          ? s.documentId
          : (s.DocumentId as number | undefined);
      if (typeof id === "number") m.set(id, s);
    });
    return m;
  }, [fileUploadStatuses]);

  const getRowId = (doc: UiUWADocument) =>
    (doc as any).uwaDocumentID ??
    (doc as any).documentID ??
    (doc as any).DocumentId ??
    0;

    const computeDisplayName = (doc: UiUWADocument) => {
      const idKey = String(getRowId(doc));
      const editedMap = methods.getValues("documentDisplayEditedNameMap");
    
      const base = (editedMap?.[idKey] ??
        (doc as any).documentDisplayFileName ??
        ""
      ).trim();
    
      const ext =
        (doc as any).fileExt ??
        (doc as any).documentFileExtension ??
        "";
      return ext ? `${base}.${ext}` : base;
    };
    
  const computeDisplayBase = (doc: UiUWADocument) => {
    const candidate = computeDisplayName(doc);
    const { base } = splitBaseAndExt(candidate);
    return (base || candidate || "").trim();
  };

  const handleEditClick = (rowDoc: UiUWADocument) => {
    const id = getRowId(rowDoc);
    setEditRowId(id);
    setCurrentEditingRow(rowDoc);

    const base = computeDisplayBase(rowDoc);
    originalBaseRef.current = base;

    methods.reset(
      { ...methods.getValues(), documentDisplayFileNameSendDoc: base },
      { keepErrors: false, keepDirty: false }
    );
  };

  const handleCancelClick = () => {
    setEditRowId(null);
    setCurrentEditingRow(null);
    originalBaseRef.current = "";
    methods.reset(
      { ...methods.getValues(), documentDisplayFileNameSendDoc: "" },
      { keepErrors: false, keepDirty: false }
    );
  };
  const doSaveLocal = (documentId: number, newBaseName: string) => {
    const rowDoc = currentEditingRow;
    if (!rowDoc) return;
  
    const docAny = rowDoc as any;
  
    const extFromDoc =
      docAny.fileExt ??
      docAny.documentFileExtension ??
      "";
  
    const trimmedBase = newBaseName.trim();
    const newFullName = extFromDoc
      ? `${trimmedBase}.${extFromDoc.toLowerCase()}`
      : trimmedBase;
  
    setRows((prev) =>
      prev.map((d) => {
        const id = getRowId(d);
        if (id !== documentId) return d;
  
        const updated: any = {
          ...d,
          fileName: newFullName,
          displayFileName: newFullName,
  
          documentDisplayFileName: trimmedBase,
        };
  
        if ((d as any).DocumentDisplayFileName !== undefined) {
          updated.DocumentDisplayFileName = trimmedBase;
        }
        if ((d as any).fileExt !== undefined) {
          updated.fileExt = extFromDoc.toLowerCase();
        }
        if ((d as any).documentFileExtension !== undefined) {
          updated.documentFileExtension = extFromDoc.toLowerCase();
        }
  
        return updated as UiUWADocument;
      })
    );
  
    const key = String(documentId);
    const curr = methods.getValues("documentDisplayEditedNameMap") ?? {};
    methods.setValue(
      "documentDisplayEditedNameMap",
      { ...curr, [key]: trimmedBase },
      { shouldDirty: true }
    );
  
    handleOnRename?.(documentId, newFullName);
      handleCancelClick();
  };
  
  const onSaveClick = (documentId: number) => async () => {
    doSaveLocal(documentId, documentDisplayFileNameSendDoc);
    await save(true, false);
  };
  
  const mapRequiredApiUWADocument = (document: UiUWADocument) => {
    const apiDocument: ApiUWADocument = {
      IsDirty: true,
      IdentityKey: document.uwaDocumentID,
      UWADocumentID: document.uwaDocumentID,
      UWARequestID: document.uwaRequestID,
      FileID: document.fileID,
      OrderID: document.orderID,
      IntegrationKey: document.integrationKey,
      OrigCreatedBy: document.origCreatedBy,
      DocumentCreatedDate: document.documentCreatedDate,
      FilePath: document.filePath,
      FileName: document.documentOriginalFileName,
      DocumentDisplayFileName : documentDisplayFileNameSendDoc,
      FileExt: document.fileExt,
      DocumentStatusTypeCode: document.documentStatusTypeCode,
      DisplayFileName :documentDisplayFileNameSendDoc,
    }

    return apiDocument;
  }

  async function save(includedFile: boolean = false, exitAfterSave: boolean = false) {
    if (currentEditingRow) {
      openAutomaticProgressDialog(
        UIConstants.SAVE_TITLE_SEARCH_DOCUMENT_IN_PROGRESS_MESSAGE
      );
      setUWAGridInEditMode(false);

      const apiDocument = mapRequiredApiUWADocument(currentEditingRow);
      // 11/21/2025 - No need to add file extension to make it consistent with Admin for UWA
      // apiDocument.DisplayFileName = `${fileName}.${currentEditingRow?.fileExt?.toLowerCase()}`;

      // TODO rlo 10/21/2025 - potentially need to call different saveUWADocument or adding parameter
      const result = await saveUWADocument(apiDocument);

      if (result.status === ActionResultStatus.Success) {
        closeAutomaticProgressDialog();
        showToast({
          type: 'success',
          title: SAVE_DOCUMENT_NAME_SUCCESS_TITLE
        });
      } else {
        closeAutomaticProgressDialog();
        // show error here
        const errorMessage = result.error?.response?.data
          ? result.error.response.data
          : result.error.response.statusText;

        setSaveErrorMessage(errorMessage);
        setOpenSaveErrorDialog(true);
      }
    }
  }

  const watched = methods.watch("documentDisplayFileNameSendDoc");
  const disableSave =
    !watched?.trim() ||
    !!methods.formState.errors.documentDisplayFileNameSendDoc ||
    watched.trim() === originalBaseRef.current;

    React.useEffect(() => {
      onEditModeChange?.(editRowId !== null);
    }, [editRowId, onEditModeChange]);
  
    
  const onSaveErrorCancelHandler = () => {
    setOpenSaveErrorDialog(false);
  };

  const onSaveErrorOkHandler = () => {
    setOpenSaveErrorDialog(false);
  };

  return (
    <FormProvider {...methods}>
      <StyledTableContainer className={className}>
        <Table
          className="file-upload-status-table"
          sx={{
            tableLayout: "fixed",
            width: "100%",        
            maxWidth: "100%",
            wordBreak: "break-word",
          }}
        >
          <TableBody>
            {rows.map((doc) => {
              const documentId = getRowId(doc);
              const isEditing = editRowId === documentId;

              const status = statusById.get(documentId);
              const isUploadSuccess =
                status?.isUploadSuccess ?? (status as any)?.IsUploadSuccess ?? true;
              const errorMessage =
                status?.errorMessage ?? (status as any)?.ErrorMessage ?? "";

              const titleForCell = computeDisplayName(doc);

              return (
                <StyledTableRow
                  key={documentId}
                  className={isEditing ? "is-editing" : undefined}
                >
                  <StyledTableCell className="file-name">
                    {isEditing ? (
                      <Controller
                        name="documentDisplayFileNameSendDoc"
                        control={methods.control}
                        render={({ field }) => (
                          <EditField
                            {...field}
                            maxLength={256}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              field.onChange(e);
                            }}
                          />
                        )}
                      />
                    ) : (
                      <TooltipCell title={titleForCell} />
                    )}
                  </StyledTableCell>

                  {!hideMessage && (
                    <StyledTableCell className="error-message">
                      <TooltipCell title={errorMessage} />
                    </StyledTableCell>
                  )}

                  <StyledTableCell className="delete-file" align="right">
                    {!isEditing && (
                      <CustomTooltip title="Edit" placement="top" arrow>
                        <IconButton
                          aria-label="Edit"
                          disabled={!isUploadSuccess}
                          onClick={() => handleEditClick(doc)}
                        >
                          <EditIcon
                            className={isUploadSuccess ? "icon-enabled" : "icon-disabled"}
                          />
                        </IconButton>
                      </CustomTooltip>
                    )}

                    {isEditing && (
                      <>
                        <CustomTooltip title="Save" placement="top" arrow>
                          <IconButton
                            aria-label="Save"
                            onClick={onSaveClick(documentId)}
                            disabled={disableSave}
                            sx={{
                              color: !disableSave ? colors.blue01 : colors.blue11,
                              "&.Mui-disabled": { color: colors.blue11 },
                            }}
                          >
                            <SaveIcon/>
                          </IconButton>
                        </CustomTooltip>

                        <CustomTooltip title="Cancel" placement="top" arrow>
                          <IconButton aria-label="Cancel" onClick={handleCancelClick}>
                            <CancelIcon className="icon-enabled" />
                          </IconButton>
                        </CustomTooltip>
                      </>
                    )}

                    {!hideDelete && !isEditing && (
                      <CustomTooltip title="Delete" placement="top" arrow>
                        <IconButton
                          aria-label="Delete"
                          disabled={!isUploadSuccess}
                          onClick={() => handleOnDelete?.(documentId)}
                        >
                          <DeleteIcon
                            className={isUploadSuccess ? "icon-enabled" : "icon-disabled"}
                          />
                        </IconButton>
                      </CustomTooltip>
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
        <ConfirmationDialogV2
        isOpen={openSaveErrorDialog}
        mainMessage={saveErrorMessage}
        singleActionButton={true}
        singleActionButtonText="OK"
        onYes={onSaveErrorOkHandler}
        onCancel={onSaveErrorCancelHandler}
      />
      </StyledTableContainer>
    </FormProvider>
  );
};

export default UploadStatusGridEdit;
